import {getAuth} from 'firebase/auth';
import {HtmlHelper} from '../../services/htmlHelper';
import {IPurchaseService} from '../../services/purchase';
import {Page} from '../page';
import {ISessionService, SessionInfo} from '../../services/session';
import {BLUEPRINT_VARIANT} from '../../const/queryParameters';
import {AuthService} from '../../services/authService';
import {EnvConfig} from '../../config/envConfig.interface';
import {ITrackingService} from '../../services/gtmHelper';
import {IUserService} from '../../services/user';
import {IBindingService} from '../../services/bindingService';

export type LandingPageConfig = {
  // preload products when on the landingpage
  preloadProducts?: boolean;

  /**
   * Default blueprint variant (may be overridden via a URL parameter).
   * If not set the default (in config file) will be used.
   */
  defaultBlueprintVariant?: string;

  /**
   * When leaving the page by clicking on a purchase button, should the variant be maintained?
   * If false (default), the default variant (for that page) will be used.
   * Does not affect other landing pages. E.g. when visiting a landingpage with a custom variant and
   * this property set to true, visiting other landingpages later will reset the variant.
   */
  redirectPurchaseMaintainBlueprintVariant?: boolean;
};

export class LandingPage extends Page {
  private readonly config: LandingPageConfig;

  constructor(
    protected landingPageConfig: LandingPageConfig,
    protected configuration: EnvConfig,
    protected authService: AuthService,
    protected userService: IUserService,
    protected trackingService: ITrackingService,
    protected sessionService: ISessionService,
    protected bindingService: IBindingService,
    protected purchaseService: IPurchaseService,
  ) {
    super(
      configuration,
      authService,
      bindingService,
      userService,
      trackingService,
    );
    this.config = landingPageConfig;
    if (this.config.preloadProducts) {
      void this.initWithPurchase();
    }
  }

  public render(): void {
    // always set or reset
    this.setMaintainBpVariant();

    if (this.config.preloadProducts) {
      // when configured, fetch the products already on the landingpage to have them in cache.
      // This should be refreshed every time the auth state changes (e.g. logout).
      getAuth().onAuthStateChanged(() => {
        void this.fetchBlueprints();
      });
    } else {
      throw new Error('classic Landingpage without purchase not implemented.');
    }
  }

  private setMaintainBpVariant() {
    let maintain =
      this.config.redirectPurchaseMaintainBlueprintVariant ?? false;
    if (
      maintain &&
      !this.config.defaultBlueprintVariant &&
      !HtmlHelper.instance.getUrlQueryParam(BLUEPRINT_VARIANT)
    ) {
      // force-reset to false if no specific override variant is specified.
      maintain = false;
    }

    this.sessionService.setBool(
      SessionInfo.purchase_maintainBpVariant,
      maintain,
    );
  }

  private async initWithPurchase(): Promise<void> {
    await this.alwaysForceUser();
  }

  /**
   * Fetch the blueprints so that they are already in cache.
   * When then fetching the products again on the product-select page,
   * no API request is required and the products are available faster.
   * @private
   */
  private async fetchBlueprints(): Promise<void> {
    try {
      const variant =
        HtmlHelper.instance.getUrlQueryParam(BLUEPRINT_VARIANT) ??
        this.config.defaultBlueprintVariant ??
        null;

      // fetch the products (this automatically stores them in cache as well)
      await this.purchaseService.getProducts(variant);
    } catch (err) {
      console.error('could not fetch products:', err);
    }
  }
}
